import React, {useState} from 'react';
import b_ from 'b_';

import I18N from 'assets/i18n/brandlift-project';

import {useAudienceTargeting} from 'client/common/hooks/audience-targeting';
import {useIsSuperuser, useIsSupport} from 'client/common/hooks/user';
import {TargetingsProps} from 'client/common/components/audience-settings/types';
import {
    convertToLegoOptions,
    getCanUsePopulationSelect,
    selectAllOptionsWhenCountrySelected,
    getTargetingLabels
} from 'client/common/components/audience-settings/helpers';
import {
    AgeCategory,
    AudienceCategoryValue,
    AudienceType,
    GenderCategory
} from 'client/common/types';
import BlockInput from 'client/common/components/block-input';
import {Select, Textarea} from 'client/common/components/lego';
import HorizontalSelector from 'client/common/components/horizontal-selector';
import Interests from 'client/common/components/audience-settings/interests';
import Quotas from 'client/common/components/audience-settings/quotas';
import Segments from 'client/common/components/audience-settings/segments';
import AudienceTargetingCategories from 'client/common/components/audience-settings/targeting-tabs/targeting-categories';
import AudienceTargetingGeo from 'client/common/components/audience-settings/targeting-tabs/targeting-geo';
import {TargetingOptions} from 'client/common/utils/audience';
import {getIsCreativeBox} from 'client/common/utils/box';

const b = b_.with('audience-settings');
const i18n = I18N.common.audienceSettings.audienceTargeting;

const SegmentPopup = () => {
    return (
        <div>
            <h5>{i18n.segmentsHelpTitle}</h5>
            <div dangerouslySetInnerHTML={{__html: i18n.segmentsHelpDescription}} />
        </div>
    );
};

// eslint-disable-next-line complexity,max-statements
export default function TargetingTabStandard(props: TargetingsProps) {
    const {boxType, request, categories, interests, onChange, disabled} = props;

    const {
        countriesOptions,
        selectedCountry,
        handleCountryChangeEvent,
        handleInterestChange,
        handleGeoChange,
        handleDescriptionChange,
        handleTypeChange,
        handleAgeChange,
        handleSegmentsChange,
        handleQuotasChange,
        handleGenderChange,
        audienceTypeOptions,
        selectedAudienceType,
        description,
        defaultInterest,
        interest,
        selectedSegments,
        defaultSegments
    } = useAudienceTargeting({
        boxType,
        request,
        categories,
        interests,
        onChange
    });

    const targetingValues = TargetingOptions.getTargetingValues(request);
    const targetingOptions = TargetingOptions.getTargetingOptions({
        categories,
        boxType,
        country: selectedCountry
    });

    const isSuperuser = useIsSuperuser();
    const isSupport = useIsSupport();
    const isCreativeBox = getIsCreativeBox(boxType);

    const defaultValues = TargetingOptions.getDefaultValues({categories, boxType, selectedCountry});

    const [selectedGeo, setSelectedGeo] = useState<string[]>(
        targetingValues.geo ?? defaultValues.geo
    );
    const [selectedGender, setSelectedGender] = useState<GenderCategory['value']>(
        (targetingValues.gender ?? defaultValues.gender) as GenderCategory['value']
    );
    const [selectedAge, setSelectedAge] = useState(targetingValues.age ?? defaultValues.age);

    const targetingLabels = getTargetingLabels(targetingOptions, {
        age: selectedAge,
        gender: selectedGender as string[],
        geo: selectedGeo
    });

    const canUsePopulationSelect =
        isCreativeBox && getCanUsePopulationSelect(selectedGeo, categories);

    const selectedGeoValue = selectAllOptionsWhenCountrySelected(targetingOptions.geo, selectedGeo);

    const handleGeoSelect = (value: AudienceCategoryValue[]) => {
        setSelectedGeo(value);
        void handleGeoChange(value);
    };

    const handleGenderSelect = (value: GenderCategory['value']) => {
        setSelectedGender(value);
        void handleGenderChange(value);
    };

    const handleAgeSelect = (value: AgeCategory['value']) => {
        setSelectedAge(value);
        void handleAgeChange(value);
    };

    const handleCountryChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedGeo([e.target.value]); // тут в value передается geo страны
        await handleCountryChangeEvent(e);
    };

    const audienceTypeOptionsEnriched = audienceTypeOptions.map(audience => {
        if (
            !defaultSegments.length &&
            !selectedSegments.length &&
            audience.value === AudienceType.YaAudience
        ) {
            return {
                ...audience,
                disabled: true,
                detailsPopup: <SegmentPopup />
            };
        }

        return audience;
    });

    let actualAge = selectedAge;
    let actualGender = selectedGender;

    if (targetingValues.age === undefined) {
        actualAge = defaultValues.age;
    }

    if (targetingValues.gender === undefined) {
        actualGender = defaultValues.gender as GenderCategory['value'];
    }

    return (
        <>
            {countriesOptions.length > 1 && selectedAudienceType !== AudienceType.YaAudience && (
                <BlockInput title={i18n.countryTitle}>
                    <Select
                        className={b('country-select')}
                        onChange={handleCountryChange}
                        options={convertToLegoOptions(countriesOptions)}
                        value={selectedCountry}
                        disabled={disabled}
                        size='m'
                        width='max'
                        view='dl-clear'
                    />
                </BlockInput>
            )}

            {/* показываем тумблер если больше 1го "таба" */}
            {audienceTypeOptions.length > 1 && (
                <div style={{marginTop: '24px'}}>
                    <HorizontalSelector
                        options={audienceTypeOptionsEnriched}
                        initialSelected={selectedAudienceType}
                        theme='default'
                        onChange={handleTypeChange}
                        disabled={disabled}
                    />
                </div>
            )}

            {audienceTypeOptions.length > 0 && selectedAudienceType === AudienceType.RespParams && (
                <AudienceTargetingCategories
                    disabled={disabled}
                    isCreativeBox={isCreativeBox}
                    selectedGeo={selectedGeo}
                    handleGeoSelect={handleGeoSelect}
                    targetingOptions={targetingOptions}
                    selectedGeoValue={selectedGeoValue}
                    handleAgeSelect={handleAgeSelect}
                    selectedAge={actualAge}
                    handleGenderSelect={handleGenderSelect}
                    selectedGender={actualGender}
                />
            )}

            {selectedAudienceType === AudienceType.MediaTargeting && (
                <>
                    <div className={b('audience-type-wrapper')}>
                        <BlockInput title={i18n.interestsTitle}>
                            <Interests
                                selected={interest ?? defaultInterest}
                                onChange={handleInterestChange}
                                interests={interests}
                                disabled={disabled}
                            />
                        </BlockInput>
                    </div>
                </>
            )}

            {selectedAudienceType === AudienceType.Manual && (
                <div className={b('audience-type-wrapper', {with: 'margin'})}>
                    <Textarea
                        placeholder={i18n.placeholder.manualDescription}
                        size='m'
                        view='dl'
                        defaultValue={description}
                        disabled={!(isSuperuser || isSupport)}
                        onBlur={handleDescriptionChange}
                        className={b('description-input')}
                        rows={4}
                    />
                </div>
            )}

            {request.type === AudienceType.Quotas && (
                <div className={b('audience-type-wrapper', {with: 'margin'})}>
                    <Quotas quotas={request.quotas || []} updateQuotas={handleQuotasChange} />
                </div>
            )}

            {request.type === AudienceType.YaAudience && (
                <>
                    <div className={b('audience-type-wrapper', {with: 'margin'})}>
                        <Segments
                            disabled={disabled}
                            selected={selectedSegments ?? defaultSegments}
                            onChange={handleSegmentsChange}
                        />
                    </div>
                </>
            )}

            {selectedAudienceType === 'GEO' && (
                <div className={b('audience-type-wrapper')}>
                    <BlockInput title={i18n.geoTitle}>
                        <AudienceTargetingGeo
                            categories={categories}
                            canUsePopulationSelect={canUsePopulationSelect}
                            disabled={disabled}
                            selectedGeo={selectedGeo}
                            handleGeoChange={handleGeoSelect}
                            targetingGeoLabels={targetingLabels.geo}
                            selectedGeoValue={selectedGeoValue}
                            targetingGeoOptions={targetingOptions.geo}
                        />
                    </BlockInput>
                </div>
            )}
        </>
    );
}
